import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import {
  PageContainer,
  DataTable,
  TableToolbar,
  StatusChip,
  formatCurrency,
} from 'components/shared';
import { useSnack, SaveSnackbar } from './settingsHelper';

const emptyMethod = { name: '', cost: '', estimatedDays: '', status: 'active' };

const ShippingMethod = () => {
  const { data, isFetching } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading: saving }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [methods, setMethods] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(emptyMethod);

  useEffect(() => {
    const saved = settings.custom?.shippingMethods;
    if (Array.isArray(saved)) setMethods(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const persist = async (next) => {
    setMethods(next);
    try {
      await updateSettings({ custom: { shippingMethods: next } }).unwrap();
      showSuccess('Shipping methods saved');
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  const openAdd = () => {
    setEditIndex(null);
    setForm(emptyMethod);
    setDialogOpen(true);
  };

  const openEdit = (row, index) => {
    setEditIndex(index);
    setForm({ ...emptyMethod, ...row });
    setDialogOpen(true);
  };

  const handleDelete = (index) => {
    persist(methods.filter((_, i) => i !== index));
  };

  const handleDialogSave = () => {
    const cleaned = {
      name: form.name.trim(),
      cost: Number(form.cost) || 0,
      estimatedDays: form.estimatedDays,
      status: form.status,
    };
    if (!cleaned.name) return;
    const next =
      editIndex === null
        ? [...methods, cleaned]
        : methods.map((m, i) => (i === editIndex ? cleaned : m));
    setDialogOpen(false);
    persist(next);
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Method Name' },
      {
        field: 'cost',
        headerName: 'Cost',
        align: 'right',
        render: (r) => formatCurrency(r.cost),
      },
      {
        field: 'estimatedDays',
        headerName: 'Estimated Delivery',
        render: (r) => (r.estimatedDays ? `${r.estimatedDays}` : '—'),
      },
      {
        field: 'status',
        headerName: 'Status',
        render: (r) => <StatusChip status={r.status} />,
      },
      {
        field: 'actions',
        headerName: '',
        align: 'right',
        render: (r, i) => (
          <>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(r, i)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => handleDelete(i)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods]
  );

  return (
    <PageContainer
      title="Shipping Methods"
      subtitle="Configure the shipping options available at checkout"
    >
      <TableToolbar
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Method
          </Button>
        }
      />
      <DataTable
        columns={columns}
        rows={methods}
        loading={isFetching}
        emptyText="No shipping methods configured yet"
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editIndex === null ? 'Add Shipping Method' : 'Edit Shipping Method'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Method Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Cost"
              type="number"
              value={form.cost}
              onChange={(e) => setForm({ ...form, cost: e.target.value })}
              fullWidth
            />
            <TextField
              label="Estimated Delivery (e.g. 3-5 days)"
              value={form.estimatedDays}
              onChange={(e) => setForm({ ...form, estimatedDays: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSave} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <SaveSnackbar snack={snack} onClose={closeSnack} />
    </PageContainer>
  );
};

export default ShippingMethod;
